<template>
	<div class="flex flex-col w-[calc(100%-60px)] sm:max-w-[920px] md:max-w-[1400px] items-center mx-[30px]">
		<div
			class="relative flex flex-col items-center bg-white border border-dj-gray-light rounded-[30px] w-full z-[15]"
		>
			<!-- searchbar -->
			<div
				v-if="loaded"
				class="flex flex-col md:flex-row items-center min-h-[100px] w-full py-5 md:py-0 pl-5 md:pl-4 pr-5 md:pr-0"
			>
				<SearchbarCompaniesAutocomplete
					v-model="keyword"
					:placeholder="$t('companies.searchbar.search.placeholder')"
					:hits-per-page="30"
				/>
				<div class="hidden md:flex divider"></div>
				<SearchbarCompaniesIndustry
					v-model="industry"
					:filterList="filters.industry.data"
					:placeholder="$t('companies.searchbar.industry.placeholder')"
				/>
				<div class="hidden md:flex divider"></div>
				<SearchbarCompaniesLocation
					v-model="location"
					:filterList="filters.country.data"
					:placeholder="$t('companies.searchbar.location.placeholder')"
				/>

				<div class="hidden md:flex divider"></div>
				<SearchbarCompaniesSize
					v-model="size"
					:filterList="filters['company-size'].data"
					:placeholder="$t('companies.searchbar.size.placeholder')"
				/>
				<div class="hidden md:flex divider"></div>
				<SearchbarCompaniesExtra
					v-model="groupExtra"
					:filterList="{ badge: filters.badge.data, szmd: filters.szmd }"
					:placeholder="$t('companies.searchbar.extra.placeholder')"
				/>

				<div
					@click="runSearch"
					class="flex justify-center items-center mt-2 md:mt-0 ml-auto md:ml-4 mr-auto md:mr-0 w-full sm:w-[200px] md:w-dj-60 h-dj-60 text-white bg-dj-blue rounded-normal cursor-pointer"
				>
					<BaseIcon class="text-[24px]" icon="ic:outline-search" color="white" />
					<span class="ml-2 md:hidden">{{ $t("search") }}</span>
				</div>
			</div>
			<div
				v-else
				class="flex items-center min-h-[100px] rounded-[30px] w-full bg-dj-gray-minimal animate-pulse"
			></div>
		</div>
		<div class="relative flex flex-col md:flex-row items-center w-full mt-5">
			<BaseButton
				@click="resetSearch"
				class="md:ml-auto mt-2 md:mt-0 mr-2 text-dj-gray"
				type="secondary"
				size="small"
				rounded
			>
				{{ $t("companies.reset") }}
			</BaseButton>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import axios from "axios";

import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "SearchbarCompanies",
	emits: ["setParamValues"],
	async setup(props, { emit }) {
		const { locale, t } = useI18n();

		const router = useRouter();
		const route = useRoute();

		const loaded = ref(false);
		const firstParamEmit = ref(true);
		const filters = ref([]);
		const keyword = ref("");
		const industry = ref([]);
		const location = ref([]);
		const size = ref([]);
		const groupExtra = ref({});

		watch(keyword, () => {
			if (firstParamEmit.value) {
				emit("setParamValues", searchParamValues.value);
				firstParamEmit.value = false;
			}
		});

		const searchParamValues = computed(() => {
			let title = "";

			if (keyword.value.length > 0) {
				title = title.concat('"', keyword.value, '"');
			}

			if (industry.value.length > 0) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(industry.value.map((a) => a.name[locale.value]).join(", ") || "");
			}

			if (location.value.length > 0) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(location.value.map((a) => a.name[locale.value]).join(", ") || "");
			}

			if (size.value.length > 0) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(size.value.map((a) => a.name[locale.value]).join(", ") || "");
			}

			if (
				Object.keys(groupExtra.value).length > 0 &&
				groupExtra.value["extra"] &&
				groupExtra.value["extra"].length > 0
			) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(groupExtra.value["extra"].map((a) => a.name[locale.value]).join(", ") || "");
			}

			if (
				Object.keys(groupExtra.value).length > 0 &&
				groupExtra.value["szmd"] &&
				groupExtra.value["szmd"].length > 0
			) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(groupExtra.value["szmd"].map((a) => a.name[locale.value]).join(", ") || "");
			}

			if (route.query.highlightedgold) {
				title = title.length > 0 ? title.concat(", ") : title;
				title = title.concat(t("highlightedCompanies"));
			}

			return title;
		});

		watch(
			() => route.query,
			() => {
				emit("setParamValues", searchParamValues.value);
			}
		);

		function runSearch() {
			const query = {
				...(keyword.value.length != 0 && { keyword: keyword.value }),
				...(industry.value.length != 0 && { industry: industry.value.map((a) => a.id).join(",") }),
				...(location.value.length != 0 && { location: location.value.map((a) => a.id).join(",") }),
				...(size.value.length != 0 && { companysize: size.value.map((a) => a.id).join(",") }),
				...(groupExtra.value["extra"] &&
					groupExtra.value["extra"].length != 0 && {
						extra: groupExtra.value["extra"].map((a) => a.id).join(","),
					}),
				...(groupExtra.value["szmd"] &&
					groupExtra.value["szmd"].length != 0 && {
						szmd: groupExtra.value["szmd"].map((a) => a.id).join(","),
					}),
			};

			router
				.push({
					path: `/${locale.value}/companies`,
					query: query,
				})
				.then(() => {
					emit("setParamValues", searchParamValues.value);
				});
		}

		const resetFieldsData = () => {
			loaded.value = false;
			keyword.value = "";
			industry.value = [];
			location.value = [];
			size.value = [];
			groupExtra.value = {};

			setTimeout(() => {
				loaded.value = true;
				emit("setParamValues", searchParamValues.value);
			}, 100);
		};

		function resetSearch() {
			router.push({
				path: `/${locale.value}/companies`,
			});

			resetFieldsData();
		}

		onMounted(() => {
			axios(globalConfig.API + `/filters/company?region=${useRegion.getCurrentRegion().code.toLowerCase()}`).then(
				(response) => {
					filters.value = response.data;
					loaded.value = true;

					setTimeout(() => {
						emit("setParamValues", searchParamValues.value);
					}, 200);

					setTimeout(() => {
						firstParamEmit.value = false;
					}, 800);
				}
			);
		});

		return {
			loaded,
			filters,
			keyword,
			industry,
			location,
			size,
			groupExtra,
			runSearch,
			resetFieldsData,
			resetSearch,
		};
	},
});
</script>

<style lang="scss" scoped>
.divider {
	@apply w-[1px] bg-dj-gray-light py-[30px];
}
</style>
